/**
 * Shared security-portal institution routing (CNS vs ANR).
 * This build is the ANR portal.
 */

export type Institution = 'cns' | 'anr' | 'unknown';

/** This build is the ANR portal. */
export const PORTAL_INSTITUTION: Institution = 'anr';

export const PORTAL_BRAND = {
  shortName: 'ANR',
  fullName: 'ANR Dashboard',
  subtitle: 'Agence Nationale de Renseignements — Surveillance institutionnelle',
  organizationHint: 'ANR',
} as const;

/** Prod defaults; DEV hosts use matching dev* origins at runtime. */
const PROD_ORIGINS: Record<'cns' | 'anr', string> = {
  cns: 'https://securite.dief360.com',
  anr: 'https://anr.dief360.com',
};

const DEV_ORIGINS: Record<'cns' | 'anr', string> = {
  cns: 'https://devcns.dief360.com',
  anr: 'https://devanr.dief360.com',
};

function resolveInstitutionOrigins(): Record<'cns' | 'anr', string> {
  if (typeof window === 'undefined') return PROD_ORIGINS;
  const host = window.location.hostname || '';
  if (host.startsWith('dev') || host.includes('.dev') || host === 'localhost' || host === '127.0.0.1') {
    return DEV_ORIGINS;
  }
  return PROD_ORIGINS;
}

export const INSTITUTION_ORIGINS: Record<'cns' | 'anr', string> = resolveInstitutionOrigins();

const CNS_ROLES = new Set([
  'cns',
  'cns_officer',
  'cns_admin',
  'cns_supervisor',
  'cns_analyst',
  'CNS_OFFICER',
  'CNS_ADMIN',
  'CNS_SUPERVISOR',
  'CNS_ANALYST',
]);

const ANR_ROLES = new Set([
  'anr',
  'anr_officer',
  'anr_admin',
  'anr_supervisor',
  'anr_analyst',
  'ANR_OFFICER',
  'ANR_ADMIN',
  'ANR_SUPERVISOR',
  'ANR_ANALYST',
]);

export function institutionFromRole(role?: string | null): Institution {
  if (!role) return 'unknown';
  const r = role.trim();
  if (CNS_ROLES.has(r) || CNS_ROLES.has(r.toLowerCase()) || r.toUpperCase().startsWith('CNS')) {
    return 'cns';
  }
  if (ANR_ROLES.has(r) || ANR_ROLES.has(r.toLowerCase()) || r.toUpperCase().startsWith('ANR')) {
    return 'anr';
  }
  const upper = r.toUpperCase();
  if (upper.includes('ANR') || upper.includes('RENSEIGNEMENT')) return 'anr';
  if (upper.includes('CNS') || upper.includes('SECURITE') || upper.includes('SÉCURITÉ')) return 'cns';
  return 'unknown';
}

export function institutionFromUser(user?: { role?: string; organization?: string } | null): Institution {
  const byRole = institutionFromRole(user?.role);
  if (byRole !== 'unknown') return byRole;
  return institutionFromRole(user?.organization);
}

export function isAllowedOnThisPortal(user?: { role?: string; organization?: string } | null): boolean {
  const inst = institutionFromUser(user);
  return inst === PORTAL_INSTITUTION;
}

export function redirectToInstitutionPortal(
  token: string,
  user: { role?: string; organization?: string; email?: string; id?: string; fullName?: string; name?: string },
  options?: { mustChangePassword?: boolean }
): boolean {
  const inst = institutionFromUser(user);
  if (inst === 'unknown' || inst === PORTAL_INSTITUTION) {
    return false;
  }
  if (typeof window === 'undefined') return false;

  const target = INSTITUTION_ORIGINS[inst];
  const host = window.location.hostname;
  if (!host.endsWith('dief360.com') && host !== 'localhost' && host !== '127.0.0.1') {
    return false;
  }
  if (host === 'localhost' || host === '127.0.0.1') {
    return false;
  }

  const payload = {
    token,
    user,
    ts: Date.now(),
    mustChangePassword: !!options?.mustChangePassword,
    must_change_password: !!options?.mustChangePassword,
  };
  const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
  window.location.href = `${target}/sso#p=${encoded}`;
  return true;
}
